/**
 * Floating action button — mobile shell's "+" to open AddTxScreen.
 *
 * Pixel-faithful port of the FAB in `design/project/shared.jsx`:
 *   - 60×60 pill, terracotta accent, cream-50 ring
 *   - Sits centered, lifted ABOVE the tab bar (translateY(-28px) from the
 *     bar top) so it does NOT overlap any of the 5 tab labels. Bug A fix:
 *     previous positioning placed the FAB inside the bar, where its 60px
 *     diameter straddled the boundary between the spacer cell and the
 *     "分析" tab and visually covered that label.
 *
 * The FAB's center is positioned at the top edge of the tab bar so the
 * lower half of the button overlaps the bar (matching the design's notch
 * silhouette) while the icon itself sits above the bar's icon row,
 * leaving every tab label fully visible.
 */
import Icon from '@/components/common/Icon';
import { haptic } from '@/lib/haptics';

/**
 * The mobile tab bar is 90px tall (see TabBar.tsx). To place the FAB
 * with its center at the bar's top edge, position its bottom at
 *   barHeight - fabRadius = 90 - 30 = 60
 * above the safe-area inset.
 */
const TAB_BAR_HEIGHT = 90;
const FAB_RADIUS = 30;
const FAB_BOTTOM_PX = TAB_BAR_HEIGHT - FAB_RADIUS; // 60

export interface FABProps {
  onClick: () => void;
  ariaLabel?: string;
}

export default function FAB({ onClick, ariaLabel = '新增紀錄' }: FABProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('tap');
        onClick();
      }}
      aria-label={ariaLabel}
      data-testid="mobile-fab"
      style={{
        position: 'fixed',
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${FAB_BOTTOM_PX}px)`,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 60,
        height: 60,
        borderRadius: 30,
        background: 'var(--accent)',
        color: '#fff',
        border: '3px solid var(--cream-50)',
        cursor: 'pointer',
        zIndex: 25,
        boxShadow:
          '0 6px 16px rgba(155,94,64,0.35), 0 2px 4px rgba(58,45,31,0.2)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Icon name="plus" size={26} stroke="#fff" strokeWidth={2.4} />
    </button>
  );
}
